import { useCallback, useEffect, useRef, useState } from 'react'

import type { SubArea } from '@ai-color-modules/simulation/types'

import type { ImageMetrics, OriginalDimensions } from './useImageMetrics'
import { getImageMetrics } from './utils/imageMetrics'

interface UseSubAreaSelectionOptions {
  getMetrics: () => ImageMetrics
  originalDimensions: OriginalDimensions | null
}

/** 選択中の頂点。polygonIndex: -1 = points (メインポリゴン), 0以上 = polygons[n] */
interface SelectedVertex {
  pointIndex: number
  polygonIndex: number
}

/** undo 履歴の最大保持数 (積算エディタの MAX_UNDO_STACK と同じ値) */
const MAX_HISTORY = 30

/**
 * サブエリア選択処理のhook
 * 画像上でクリック・ドラッグしてポリゴンを描画し、サブエリアを選択する
 */
export const useSubAreaSelection = ({
  getMetrics,
  originalDimensions,
}: UseSubAreaSelectionOptions) => {
  const [subAreas, setSubAreas] = useState<SubArea[]>([])
  const [selectedSubAreaIndex, setSelectedSubAreaIndex] = useState<number>(0)
  const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null)
  /** ドラッグ中のポリゴンインデックス: -1 = points (メインポリゴン), 0以上 = polygons[n] */
  const [draggingPolygonIndex, setDraggingPolygonIndex] = useState<number>(-1)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [hasDragged, setHasDragged] = useState<boolean>(false)
  const skipNextClickRef = useRef<boolean>(false)
  /** 編集操作の undo 履歴 (subAreas 全体のスナップショット。immutable 更新なので参照保持で安全) */
  const [history, setHistory] = useState<SubArea[][]>([])
  /**
   * 現在のドラッグで履歴を積んだか。state (hasDragged) は同一レンダー内の連続 move で
   * stale になり二重 push しうるため、ref で同期的にガードする
   */
  const dragHistoryPushedRef = useRef(false)
  /** 選択中の頂点 (右クリックメニュー / Delete キー削除の対象) */
  const [selectedVertex, setSelectedVertex] = useState<SelectedVertex | null>(null)

  const pushHistory = useCallback((snapshot: SubArea[]) => {
    setHistory(prev => [...prev.slice(-(MAX_HISTORY - 1)), snapshot])
  }, [])

  // 編集対象エリアが変わったら頂点選択を解除
  useEffect(() => {
    setSelectedVertex(null)
  }, [selectedSubAreaIndex])

  const startDragging = (pointIndex: number, polygonIndex = -1) => {
    setDraggingPointIndex(pointIndex)
    setDraggingPolygonIndex(polygonIndex)
    setIsDragging(true)
    setHasDragged(false)
    dragHistoryPushedRef.current = false
  }

  /**
   * ポイントのドラッグ開始処理
   * タップ/クリック直後にドラッグモードに入る
   * @param polygonIndex -1 = points (メインポリゴン), 0以上 = polygons[n]
   */
  const handleDragStart = (
    e: React.MouseEvent | React.TouchEvent,
    pointIndex: number,
    polygonIndex = -1,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    startDragging(pointIndex, polygonIndex)
    // 頂点の押下 = 頂点選択 (選択モードの「頂点選択」)
    setSelectedVertex({ pointIndex, polygonIndex })
    skipNextClickRef.current = true
  }

  const handleDragEnd = () => {
    if (isDragging) {
      setIsDragging(false)
      setDraggingPointIndex(null)
      setDraggingPolygonIndex(-1)
      skipNextClickRef.current = true
      window.setTimeout(() => {
        skipNextClickRef.current = false
      }, 0)
    } else {
      skipNextClickRef.current = false
    }
  }

  /**
   * オーバーレイ移動時の処理
   */
  const handleOverlayMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    // mouseup を取りこぼして isDragging が残った場合の自己修復
    // (ボタンが離れているのにホバーで頂点が動き続けるのを防ぐ)
    if (isDragging && 'buttons' in e && (e.buttons & 1) === 0) {
      handleDragEnd()
      return
    }
    if ('touches' in e && isDragging) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (
      isDragging &&
      draggingPointIndex !== null &&
      selectedSubAreaIndex !== null &&
      originalDimensions
    ) {
      const touch = 'touches' in e ? e.touches[0] : undefined
      if ('touches' in e && !touch) return
      const clientX = touch?.clientX ?? ('clientX' in e ? e.clientX : 0)
      const clientY = touch?.clientY ?? ('clientY' in e ? e.clientY : 0)
      const rect = e.currentTarget.getBoundingClientRect()
      const metrics = getImageMetrics(getMetrics, originalDimensions)
      if (!metrics) return
      const { dispW, dispH } = metrics

      // rectサイズとdispサイズの比率を計算（CSS transformのscaleを考慮）
      const scaleX = rect.width / dispW
      const scaleY = rect.height / dispH

      // クライアント座標を画像座標に変換（rectは既にoffXを含んでいる）
      const imgX = (clientX - rect.left) / scaleX
      const imgY = (clientY - rect.top) / scaleY

      // 画像範囲外はスキップ
      if (imgX < 0 || imgY < 0 || imgX > dispW || imgY > dispH) return

      // オリジナル座標に変換
      const newOriginalX = (imgX / dispW) * originalDimensions.width
      const newOriginalY = (imgY / dispH) * originalDimensions.height
      if (!hasDragged) setHasDragged(true)
      if (!dragHistoryPushedRef.current) {
        dragHistoryPushedRef.current = true
        // 1 ドラッグ操作 = undo 1 ステップ。実際に動き始めた時点の状態を積む
        pushHistory(subAreas)
      }

      setSubAreas(prev => {
        const newArr = [...prev]
        const currentArea = { ...newArr[selectedSubAreaIndex] }

        if (draggingPolygonIndex === -1) {
          // メインポリゴン (points) を更新
          const newPoints = [...currentArea.points]
          newPoints[draggingPointIndex] = {
            originalX: newOriginalX,
            originalY: newOriginalY,
          }
          currentArea.points = newPoints
        } else {
          // 追加ポリゴン (polygons[n]) を更新
          const newPolygons = currentArea.polygons ? [...currentArea.polygons] : []
          if (newPolygons[draggingPolygonIndex]) {
            const newPolygonPoints = [...newPolygons[draggingPolygonIndex]]
            newPolygonPoints[draggingPointIndex] = {
              originalX: newOriginalX,
              originalY: newOriginalY,
            }
            newPolygons[draggingPolygonIndex] = newPolygonPoints
            currentArea.polygons = newPolygons
          }
        }

        newArr[selectedSubAreaIndex] = currentArea
        return newArr
      })
    }
  }

  /**
   * 選択中のエリアが結合エリアかどうかを判定
   * 結合エリア = polygons 配列が1つ以上のポリゴンを持っている
   */
  const isMergedArea = (area: SubArea): boolean => {
    return Boolean(area.polygons && area.polygons.length > 0)
  }

  /**
   * オーバーレイクリック時の処理
   * クリック位置にサブエリアのポイントを追加する
   * 注意: 結合エリアでは空白クリックによる頂点追加は無効（中間点挿入のみ許可）
   */
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // ドラッグ中またはドラッグ直後の場合は新しいポイントを追加しない
    if (isDragging || hasDragged || skipNextClickRef.current) {
      setHasDragged(false)
      skipNextClickRef.current = false
      return
    }

    if (!originalDimensions) {
      // 画像サイズが取得できていない場合は処理をスキップ
      return
    }
    if (selectedSubAreaIndex === null || subAreas.length === 0 || !subAreas[selectedSubAreaIndex]) {
      // サブエリアが存在しない場合は処理をスキップ
      return
    }

    // 結合エリアの場合は空白クリックでの頂点追加を無効化
    // （中間点設置経由での頂点追加のみ許可）
    const currentArea = subAreas[selectedSubAreaIndex]
    if (isMergedArea(currentArea)) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const metrics = getImageMetrics(getMetrics, originalDimensions)
    if (!metrics) return
    const { dispW, dispH } = metrics

    // rectサイズとdispサイズの比率を計算（CSS transformのscaleを考慮）
    const scaleX = rect.width / dispW
    const scaleY = rect.height / dispH

    // クライアント座標を画像座標に変換（rectは既にoffXを含んでいる）
    const imgX = (e.clientX - rect.left) / scaleX
    const imgY = (e.clientY - rect.top) / scaleY

    // 余白は無視
    if (imgX < 0 || imgY < 0 || imgX > dispW || imgY > dispH) return

    // オリジナル座標に変換
    const originalX = (imgX / dispW) * originalDimensions.width
    const originalY = (imgY / dispH) * originalDimensions.height

    pushHistory(subAreas)
    setSubAreas(prev => {
      const newArr = [...prev]
      const area = { ...newArr[selectedSubAreaIndex] }
      area.points = [...area.points, { originalX, originalY }]
      newArr[selectedSubAreaIndex] = area
      return newArr
    })
  }

  /**
   * 新しいサブエリアを追加する
   */
  const handleAddSubArea = () => {
    setSubAreas(prev => [
      ...prev,
      {
        id: Date.now(),
        label: `カスタムエリア${prev.length + 1}`,
        color: '',
        points: [],
        useDetectedAreaOnly: true,
      },
    ])
    setSelectedSubAreaIndex(subAreas.length)
  }

  /**
   * サブエリアをリセットする
   */
  const resetSubArea = (idx: number) => {
    setSubAreas(prev => {
      const arr = [...prev]
      arr[idx] = { ...arr[idx], color: '', points: [], useDetectedAreaOnly: true }
      return arr
    })
  }

  /**
   * サブエリアを削除する
   */
  const removeSubArea = (idx: number) => {
    setSubAreas(prev => {
      const arr = prev.filter((_, i) => i !== idx)
      // 空配列を許容（復元機能のため）
      return arr
    })
    setSelectedSubAreaIndex(0)
  }

  /**
   * サブエリアの色を設定する
   */
  const setSubAreaColor = (idx: number, color: string) => {
    setSubAreas(prev => {
      const arr = [...prev]
      arr[idx] = { ...arr[idx], color }
      return arr
    })
  }

  /**
   * サブエリアの検出領域に限定フラグを設定する
   */
  const setSubAreaUseDetectedAreaOnly = (idx: number, useDetectedAreaOnly: boolean) => {
    setSubAreas(prev => {
      const arr = [...prev]
      arr[idx] = { ...arr[idx], useDetectedAreaOnly }
      return arr
    })
  }

  /**
   * 辺上に頂点を挿入する
   * @param edgeIndex 挿入する辺のインデックス（この辺の後に挿入される）
   * @param originalX 挿入する座標X（オリジナル座標系）
   * @param originalY 挿入する座標Y（オリジナル座標系）
   * @param polygonIndex -1 = points (メインポリゴン), 0以上 = polygons[n]
   */
  const handleInsertPoint = (
    edgeIndex: number,
    originalX: number,
    originalY: number,
    polygonIndex = -1,
  ) => {
    if (selectedSubAreaIndex === null || !subAreas[selectedSubAreaIndex]) {
      return
    }

    pushHistory(subAreas)
    setSubAreas(prev => {
      const newArr = [...prev]
      const target = newArr[selectedSubAreaIndex]

      if (polygonIndex === -1) {
        // メインポリゴン (points) に挿入
        const newPoints = [...target.points]
        newPoints.splice(edgeIndex + 1, 0, { originalX, originalY })
        newArr[selectedSubAreaIndex] = { ...target, points: newPoints }
      } else {
        // 追加ポリゴン (polygons[n]) に挿入
        const newPolygons = target.polygons ? [...target.polygons] : []
        if (newPolygons[polygonIndex]) {
          const newPolygonPoints = [...newPolygons[polygonIndex]]
          newPolygonPoints.splice(edgeIndex + 1, 0, { originalX, originalY })
          newPolygons[polygonIndex] = newPolygonPoints
          newArr[selectedSubAreaIndex] = { ...target, polygons: newPolygons }
        }
      }

      return newArr
    })
  }

  /**
   * 頂点を削除する
   * @param pointIndex 削除する頂点のインデックス
   * @param polygonIndex -1 = points (メインポリゴン), 0以上 = polygons[n]
   */
  const handleDeletePoint = (pointIndex: number, polygonIndex = -1) => {
    if (selectedSubAreaIndex === null || !subAreas[selectedSubAreaIndex]) {
      return
    }

    pushHistory(subAreas)
    // 削除で頂点インデックスがズレるため選択は解除する
    setSelectedVertex(null)
    setSubAreas(prev => {
      const newArr = [...prev]
      const target = newArr[selectedSubAreaIndex]

      if (polygonIndex === -1) {
        // メインポリゴン (points) から削除
        const newPoints = [...target.points]
        newPoints.splice(pointIndex, 1)
        newArr[selectedSubAreaIndex] = { ...target, points: newPoints }
      } else {
        // 追加ポリゴン (polygons[n]) から削除
        const newPolygons = target.polygons ? [...target.polygons] : []
        if (newPolygons[polygonIndex]) {
          const newPolygonPoints = [...newPolygons[polygonIndex]]
          newPolygonPoints.splice(pointIndex, 1)

          // ポリゴンが空になったら配列から削除
          if (newPolygonPoints.length === 0) {
            newPolygons.splice(polygonIndex, 1)
          } else {
            newPolygons[polygonIndex] = newPolygonPoints
          }

          newArr[selectedSubAreaIndex] = {
            ...target,
            polygons: newPolygons.length > 0 ? newPolygons : undefined,
          }
        }
      }

      return newArr
    })
  }

  /**
   * 直前の編集操作 (頂点の追加・移動・削除) を 1 ステップ取り消す。
   *
   * 巻き戻すのは **ジオメトリ (points / polygons) だけ**。色・ラベル・有効範囲設定
   * (`useDetectedAreaOnly`) はジオメトリ編集とは独立に操作できるため、履歴 snapshot の
   * 値で上書きしない。
   *
   * snapshot を丸ごと `setSubAreas(last)` すると、「頂点を動かす → 一覧で
   * 『ベースエリア内のみ有効』を OFF にする → 直前のドラッグを undo」で OFF が無告知で
   * ON に戻る (issue #2413 で当該設定がポリゴン編集の真横のワンクリック操作になり、
   * 現実的に踏みやすくなった)。色指定も同じ構造で巻き戻っていた。
   *
   * 対応は id で取る。undo 後にエリアの増減があっても index ズレで別エリアの
   * ジオメトリを混ぜないため。
   */
  const undo = () => {
    if (history.length === 0) return
    const last = history[history.length - 1]
    setHistory(prev => prev.slice(0, -1))
    setSelectedVertex(null)
    setSubAreas(prev =>
      last.map(snapshot => {
        const current = prev.find(area => area.id === snapshot.id)
        if (!current) return snapshot
        return { ...current, points: snapshot.points, polygons: snapshot.polygons }
      }),
    )
  }

  /** undo 履歴を破棄する (タブ切替・保存データ復元時に呼ぶ) */
  const resetHistory = useCallback(() => {
    setHistory([])
    setSelectedVertex(null)
  }, [])

  /** 頂点を選択する (右クリックメニュー表示時など) */
  const selectVertex = (pointIndex: number, polygonIndex = -1) => {
    setSelectedVertex({ pointIndex, polygonIndex })
  }

  /** 選択中の頂点を削除する (Delete キー / 右クリックメニュー) */
  const deleteSelectedVertex = () => {
    if (!selectedVertex) return
    handleDeletePoint(selectedVertex.pointIndex, selectedVertex.polygonIndex)
  }

  return {
    subAreas,
    setSubAreas,
    selectedSubAreaIndex,
    setSelectedSubAreaIndex,
    draggingPointIndex,
    draggingPolygonIndex,
    isDragging,
    handleDragStart,
    handleDragEnd,
    handleOverlayMove,
    handleOverlayClick,
    handleAddSubArea,
    resetSubArea,
    removeSubArea,
    setSubAreaColor,
    setSubAreaUseDetectedAreaOnly,
    handleInsertPoint,
    handleDeletePoint,
    selectedVertex,
    selectVertex,
    deleteSelectedVertex,
    undo,
    canUndo: history.length > 0,
    resetHistory,
  }
}
